import { useEffect, useMemo, useState, useRef } from 'react';
import type {
  MRT_ColumnFiltersState,
  MRT_PaginationState,
  MRT_SortingState,
} from 'material-react-table';
import { apiClient } from '../../lib/api';
import { useDebounce } from '../../hooks/useDebounce';
import { mapStateToParams, mapCsvParams } from './mapping';
import { applyColumnFiltersToParams, type ColumnFilterLike } from './filterUtils';
import type { AflDailyRow } from './columns';
import type { DailyReportSummary } from '../../types/reportSummaries';
import type { FacetsState } from './useDynamicColumns';
import type { ProviderSource, ReportFiltersState } from './viewState';

interface UseReportDataOptions {
  date: { startDate: string; endDate: string };
  pagination: MRT_PaginationState;
  sorting: MRT_SortingState;
  globalFilter: string;
  filterState: ReportFiltersState;
  providerSource: ProviderSource;
  tenantSlug?: string;
  columnFilters: MRT_ColumnFiltersState;
  columnVisibility: Record<string, boolean>;
  columnOrder: string[];
  user: { id?: string } | null;
  userId: string;
  /** When false, data fetching is deferred (e.g. waiting for pref hydration). */
  enabled?: boolean;
}

export interface ReportDataResult {
  rows: AflDailyRow[];
  total: number;
  summary: DailyReportSummary | null;
  loading: boolean;
  error: string;
  isInitialLoad: boolean;
  facets: FacetsState;
  exportQueryParams: URLSearchParams;
}

/**
 * Manages data fetching, facet loading, and server-side pref persistence
 * for the OLSG Daily Report. Extracted from Reports.tsx.
 */
export function useReportData({
  date,
  pagination,
  sorting,
  globalFilter,
  filterState,
  providerSource,
  tenantSlug,
  columnFilters,
  columnVisibility,
  columnOrder,
  enabled = true,
}: UseReportDataOptions): ReportDataResult {
  const { typeCodeFilter, lineTypeFilter, linePayFilter, coveredLineFilter } = filterState;

  const [rows, setRows] = useState<AflDailyRow[]>([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState<DailyReportSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [facets, setFacets] = useState<FacetsState>({});

  // Clear stale data immediately on tenant change so table shows loading
  const prevTenantRef = useRef(tenantSlug);
  useEffect(() => {
    if (prevTenantRef.current !== tenantSlug) {
      prevTenantRef.current = tenantSlug;
      setRows([]);
      setTotal(0);
      setSummary(null);
      setFacets({});
      setLoading(true);
      setError('');
    }
  }, [tenantSlug]);

  // Debounced filters for query building
  const debouncedGlobalFilter = useDebounce(globalFilter, 300);
  const debouncedTypeCodeFilter = useDebounce(typeCodeFilter, 300);
  const debouncedLineTypeFilter = useDebounce(lineTypeFilter, 300);
  const debouncedLinePayFilter = useDebounce(linePayFilter, 300);
  const debouncedCoveredLineFilter = useDebounce(coveredLineFilter, 300);
  const debouncedColumnFilters = useDebounce(columnFilters, 300);

  // Debounced filters for facet fetching (longer delay)
  const debouncedTypeCodeForFacets = useDebounce(typeCodeFilter, 500);
  const debouncedLineTypeForFacets = useDebounce(lineTypeFilter, 500);
  const debouncedLinePayForFacets = useDebounce(linePayFilter, 500);
  const debouncedCoveredLineForFacets = useDebounce(coveredLineFilter, 500);
  const debouncedProviderSourceForFacets = useDebounce(providerSource, 500);

  // Build query string for data fetching
  const queryString = useMemo(() => {
    const p = mapStateToParams(date, pagination, sorting, debouncedGlobalFilter);
    if (debouncedTypeCodeFilter.length) p.set('typeCode', debouncedTypeCodeFilter.join(','));
    if (debouncedLineTypeFilter.length) p.set('lineType', debouncedLineTypeFilter.join(','));
    if (debouncedLinePayFilter.length) p.set('linePay', debouncedLinePayFilter.join(','));
    if (debouncedCoveredLineFilter.length)
      p.set('coveredLine', debouncedCoveredLineFilter.join(','));
    // if (providerSource !== 'both') p.set('providerSource', providerSource);
    p.set('providerSource', providerSource);
    applyColumnFiltersToParams(p, debouncedColumnFilters as ColumnFilterLike[]);
    return p.toString();
  }, [
    date,
    pagination,
    sorting,
    debouncedGlobalFilter,
    debouncedTypeCodeFilter,
    debouncedLineTypeFilter,
    debouncedLinePayFilter,
    debouncedCoveredLineFilter,
    debouncedColumnFilters,
    providerSource,
    tenantSlug,
  ]);

  // Fetch facets
  useEffect(() => {
    if (!enabled) return;
    const abortController = new AbortController();
    const timer = setTimeout(() => {
      const params = new URLSearchParams({
        fields:
          'Line Type,Line Pay Method,Category,Service Type,Manufacturer,TransCode,TansGroup,Special Order,Store,Op Code,coveredLine,coveredPart,Type Code,T1 Label',
      });
      if (date.startDate) params.set('startDate', date.startDate);
      if (date.endDate) params.set('endDate', date.endDate);
      if (debouncedTypeCodeForFacets.length)
        params.set('typeCode', debouncedTypeCodeForFacets.join(','));
      if (debouncedLineTypeForFacets.length)
        params.set('lineType', debouncedLineTypeForFacets.join(','));
      if (debouncedLinePayForFacets.length)
        params.set('linePay', debouncedLinePayForFacets.join(','));
      if (debouncedCoveredLineForFacets.length)
        params.set('coveredLine', debouncedCoveredLineForFacets.join(','));
      // if (debouncedProviderSourceForFacets !== 'both')
      //   params.set('providerSource', debouncedProviderSourceForFacets);
      params.set('providerSource', debouncedProviderSourceForFacets);

      apiClient
        .get(`/api/reports/daily/facets?${params.toString()}`, { signal: abortController.signal })
        .then(async (r) => {
          if (!r.ok) {
            console.warn(`Failed to fetch facets: HTTP ${r.status}`);
            return {};
          }
          return r.json();
        })
        .then((d) => {
          if (!abortController.signal.aborted) setFacets(d || {});
        })
        .catch((e) => {
          if (e.name === 'AbortError') return;
          console.warn('Error fetching facets:', e);
          if (!abortController.signal.aborted) setFacets({});
        });
    }, 500);

    return () => {
      abortController.abort();
      clearTimeout(timer);
    };
  }, [
    date,
    debouncedTypeCodeForFacets,
    debouncedLineTypeForFacets,
    debouncedLinePayForFacets,
    debouncedCoveredLineForFacets,
    debouncedProviderSourceForFacets,
    tenantSlug,
    enabled,
  ]);

  // Fetch report data
  useEffect(() => {
    if (!enabled) {
      setLoading(true); // Show loading state while waiting for hydration
      return;
    }
    const abortController = new AbortController();
    setLoading(true);
    setError('');

    const timer = setTimeout(
      () => {
        const fetchTimeout = setTimeout(() => {
          if (!abortController.signal.aborted) {
            console.error('Request timeout after 30 seconds');
            setError('Request timed out. Please try again or select a shorter date range.');
            setLoading(false);
            setRows([]);
            setTotal(0);
            setSummary(null);
            setIsInitialLoad(false);
            abortController.abort();
          }
        }, 30000);

        apiClient
          .get(`/api/reports/daily?${queryString}`, { signal: abortController.signal })
          .then(async (r) => {
            clearTimeout(fetchTimeout);
            if (!r.ok) {
              const errorText = await r.text();
              let errorMessage = `HTTP ${r.status}`;
              try {
                const errorJson = JSON.parse(errorText);
                errorMessage = errorJson.message || errorJson.error || errorMessage;
              } catch {
                errorMessage = errorText || errorMessage;
              }
              throw new Error(errorMessage);
            }
            return r.json();
          })
          .then((d) => {
            clearTimeout(fetchTimeout);
            if (!abortController.signal.aborted) {
              setRows(d.rows || []);
              setTotal(d.total || 0);
              setSummary((d.summary as DailyReportSummary) || null);
              if (d.error) setError(String(d.error));
              setIsInitialLoad(false);
            }
          })
          .catch((e) => {
            clearTimeout(fetchTimeout);
            if (e.name === 'AbortError') return;
            if (!abortController.signal.aborted) {
              console.error('Error fetching report data:', e);
              setError(String(e?.message || e));
              setRows([]);
              setTotal(0);
              setSummary(null);
              setIsInitialLoad(false);
            }
          })
          .finally(() => {
            if (!abortController.signal.aborted) setLoading(false);
          });
      },
      isInitialLoad ? 150 : 300,
    );

    return () => {
      abortController.abort();
      clearTimeout(timer);
    };
  }, [queryString, isInitialLoad, tenantSlug, enabled]);

  // Export query params (no pagination, for full export)
  const exportQueryParams = useMemo(() => {
    const p = mapCsvParams(date, columnFilters, columnVisibility, {
      columnOrder,
      sorting,
      globalFilter,
    });
    p.set('privacy', 'true');
    if (typeCodeFilter.length) p.set('typeCode', typeCodeFilter.join(','));
    if (lineTypeFilter.length) p.set('lineType', lineTypeFilter.join(','));
    if (linePayFilter.length) p.set('linePay', linePayFilter.join(','));
    if (coveredLineFilter.length) p.set('coveredLine', coveredLineFilter.join(','));
    // if (providerSource !== 'both') p.set('providerSource', providerSource);
    p.set('providerSource', providerSource);
    applyColumnFiltersToParams(p, columnFilters as ColumnFilterLike[]);
    return p;
  }, [
    date,
    columnFilters,
    columnVisibility,
    columnOrder,
    sorting,
    globalFilter,
    typeCodeFilter,
    lineTypeFilter,
    linePayFilter,
    coveredLineFilter,
    providerSource,
  ]);

  return {
    rows,
    total,
    summary,
    loading,
    error,
    isInitialLoad,
    facets,
    exportQueryParams,
  };
}
